import { useTranslation } from '../../i18n/useTranslation.js';

/**
 * Das Stundenkonto: wie viel habe ich insgesamt zugute?
 *
 * Die Frage, die vorher keine Antwort hatte. Der Saldo galt immer nur für
 * den Zeitraum, den man gerade ansah - die Ueberstunden vom März waren im
 * April nicht mehr zu sehen.
 *
 * Deshalb steht hier EINE grosse Zahl und darunter, woraus sie besteht. Ein
 * Kontostand, den man nicht nachrechnen kann, wird nicht geglaubt; ein
 * Kontostand, der erst nach drei Klicks erscheint, wird nicht gelesen.
 */

/** "+14.00" statt "14" - und ein Minus behält sein Zeichen. */
function signed(value) {
  const n = Number(value ?? 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

const vorzeichen = (n) => (n > 0 ? 'plus' : n < 0 ? 'minus' : undefined);

export function TimeAccountCard({ account, loading = false }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="time-account" aria-busy="true">
        <div className="time-account-main">
          <div className="skeleton-line" style={{ width: '8rem', height: '10px' }} />
          <div className="skeleton-line" style={{ width: '11rem', height: '32px' }} />
        </div>
      </div>
    );
  }
  if (!account) {
    return null;
  }

  /* Ohne Anfang gibt es nichts zu rechnen. Irgendeine Zahl zu zeigen wäre
     schlimmer als keine: sie sähe aus wie ein Kontostand. */
  if (account.totalHours === null || account.totalHours === undefined) {
    return (
      <div className="time-account">
        <p className="time-account-label">{t('timeAccount.title')}</p>
        <p className="muted text-sm">{t('timeAccount.noStart')}</p>
      </div>
    );
  }

  const gesamt = Number(account.totalHours);
  const fest = Number(account.closedHours ?? 0);
  const laufend = Number(account.openHours ?? 0);
  const anfang = Number(account.openingHours ?? 0);
  const kompensiert = Number(account.openCompensatedHours ?? 0);

  return (
    <div className="time-account" data-sign={vorzeichen(gesamt)}>
      <div className="time-account-main">
        <p className="time-account-label">{t('timeAccount.title')}</p>
        <p className="time-account-total num mono">
          {signed(gesamt)}
          <span className="time-account-unit">{t('timeAccount.unit')}</span>
        </p>
        <p className="muted text-sm">{t('timeAccount.asOf', { date: account.openTo })}</p>
      </div>

      {/* Woraus die Zahl besteht. Drei Posten, in der Reihenfolge, in der
          sie entstanden sind - so liest man eine Abrechnung. */}
      <dl className="time-account-parts">
        <div>
          <dt>{t('timeAccount.opening')}</dt>
          <dd className="num mono">{signed(anfang)}</dd>
        </div>
        <div>
          <dt>
            {account.closedMonths === 1
              ? t('timeAccount.closedOne')
              : t('timeAccount.closed', { count: account.closedMonths ?? 0 })}
          </dt>
          <dd className="num mono">{signed(fest)}</dd>
        </div>
        <div>
          <dt>{t('timeAccount.open', { from: account.openFrom })}</dt>
          <dd className="num mono">{signed(laufend)}</dd>
        </div>
      </dl>

      {/* Warum das Ist unter dem Soll liegt, obwohl niemand gefehlt hat.
          Ohne diesen Satz sieht ein bezogener Kompensationstag wie eine
          Lücke in der Erfassung aus. */}
      {kompensiert > 0 && (
        <p className="time-account-note">
          {t('timeAccount.compensated', { hours: kompensiert.toFixed(2) })}
        </p>
      )}

      {/* Freigegebene Monate jenseits einer Lücke. Kein Fehler - die Zahl
          stimmt, sie stammt nur nicht aus lauter festen Monaten. */}
      {account.approvedAfterGap?.length > 0 && (
        <p className="time-account-note">
          {t('timeAccount.gap', { months: account.approvedAfterGap.join(', ') })}
        </p>
      )}
    </div>
  );
}
